/**
 * EditSkillModal
 * Modal for editing a generated skill's name, description, content, and categories.
 */

import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Modal, Button, Input, Textarea } from '../../ui';
import type { GeneratedSkill } from '../../../types';
import styles from './EditSkillModal.module.css';

export interface EditSkillModalProps {
  skill: GeneratedSkill;
  onSave: (updatedSkill: GeneratedSkill) => void;
  onClose: () => void;
}

export const EditSkillModal: React.FC<EditSkillModalProps> = ({
  skill,
  onSave,
  onClose,
}) => {
  const [form, setForm] = useState<GeneratedSkill>({ ...skill });
  const [isSaving, setIsSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  /** Handle text field changes */
  const handleChange = (field: keyof GeneratedSkill, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /** Update content and auto-compute charCount */
  const handleContentChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      content: value,
      charCount: value.length,
    }));
  };

  /** Add a category */
  const addCategory = () => {
    const trimmed = newCategory.trim();
    if (trimmed && !form.categories.includes(trimmed)) {
      setForm((prev) => ({
        ...prev,
        categories: [...prev.categories, trimmed],
      }));
      setNewCategory('');
    }
  };

  /** Remove a category */
  const removeCategory = (cat: string) => {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.filter((c) => c !== cat),
    }));
  };

  /** Handle save */
  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await onSave(form);
    } finally {
      setIsSaving(false);
    }
  };

  /** Handle key press for adding categories */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCategory();
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit Skill"
      size="lg"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={!form.name.trim()}
          >
            Save Changes
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        {/* Skill Name */}
        <div className={styles.field}>
          <label className={styles.label}>Skill Name</label>
          <Input
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Skill name"
          />
        </div>

        {/* Description */}
        <div className={styles.field}>
          <label className={styles.label}>Description</label>
          <Textarea
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Describe what this skill provides..."
            rows={2}
          />
        </div>

        {/* Content */}
        <div className={styles.field}>
          <label className={styles.label}>
            Skill Content
            <span className={styles.charCounter}>{form.charCount?.toLocaleString() || 0} chars</span>
          </label>
          <Textarea
            value={form.content}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder="Enter the skill content (instructions, conventions, rules)..."
            rows={12}
            className={styles.codeTextarea}
          />
        </div>

        {/* Categories */}
        <div className={styles.field}>
          <label className={styles.label}>Categories</label>
          <div className={styles.tagInput}>
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Add category and press Enter"
            />
            <Button variant="secondary" size="sm" onClick={addCategory} disabled={!newCategory.trim()}>
              <Plus size={14} />
            </Button>
          </div>
          <div className={styles.tags}>
            {(form.categories || []).map((cat) => (
              <span key={cat} className={styles.tag}>
                {cat}
                <button className={styles.tagRemove} onClick={() => removeCategory(cat)}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditSkillModal;
